import { useState, useEffect } from 'react';

const RenewableEnergy = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('renewable-energy');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const energySolutions = [
    {
      title: 'Windmills',
      description: 'Harnessing wind power for sustainable energy generation',
      icon: 'ri-windy-line',
      image: 'https://readdy.ai/api/search-image?query=Traditional%20windmills%20in%20rural%20Indian%20farm%20setting%2C%20spinning%20wind%20turbines%20generating%20clean%20energy%2C%20peaceful%20countryside%20landscape%20with%20green%20fields%2C%20sustainable%20renewable%20energy%20infrastructure%2C%20blue%20sky%20with%20white%20clouds&width=500&height=400&seq=windmill-energy&orientation=landscape'
    },
    {
      title: 'Solar Pumps',
      description: 'Solar-powered water pumping systems for irrigation',
      icon: 'ri-sun-line',
      image: 'https://readdy.ai/api/search-image?query=Solar%20water%20pump%20system%20in%20Indian%20agricultural%20field%2C%20solar%20panels%20powering%20irrigation%20equipment%2C%20farmers%20using%20sustainable%20technology%2C%20clean%20energy%20for%20farming%2C%20modern%20agricultural%20innovation%20with%20traditional%20farming&width=500&height=400&seq=solar-pump&orientation=landscape'
    },
    {
      title: 'Rainwater Harvesting',
      description: 'Collecting and storing precious rainwater resources',
      icon: 'ri-drop-line',
      image: 'https://readdy.ai/api/search-image?query=Rainwater%20harvesting%20system%20in%20Indian%20farm%2C%20water%20collection%20tanks%20and%20channels%2C%20monsoon%20rain%20collection%20infrastructure%2C%20sustainable%20water%20management%2C%20traditional%20water%20conservation%20methods%2C%20green%20agriculture&width=500&height=400&seq=rainwater-harvest&orientation=landscape'
    },
    {
      title: 'Groundwater Recharge',
      description: 'Replenishing underground water sources naturally',
      icon: 'ri-water-flash-line',
      image: 'https://readdy.ai/api/search-image?query=Groundwater%20recharge%20system%20in%20rural%20India%2C%20water%20percolation%20pits%20and%20recharge%20wells%2C%20natural%20water%20conservation%20methods%2C%20sustainable%20farming%20practices%2C%20environmental%20restoration%2C%20water%20table%20management&width=500&height=400&seq=groundwater-recharge&orientation=landscape'
    }
  ];

  return (
    <section className="py-20 relative overflow-hidden">
      {/* Parallax Background */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-fixed"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=Panoramic%20view%20of%20renewable%20energy%20farm%20with%20windmills%20and%20solar%20panels%2C%20sustainable%20energy%20infrastructure%20in%20rural%20landscape%2C%20clean%20technology%20integration%20with%20agriculture%2C%20blue%20sky%20and%20green%20fields%2C%20environmental%20harmony&width=1920&height=1080&seq=renewable-bg&orientation=landscape')`
        }}
      >
        <div className="absolute inset-0 bg-blue-900/80"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-16 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Renewable Energy & Water
          </h2>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Powering our farm with clean, sustainable energy and responsible water management
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {energySolutions.map((solution, index) => (
            <div 
              key={index}
              className={`bg-white/95 backdrop-blur-sm rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}
              style={{ transitionDelay: `${200 + index * 150}ms` }}
            >
              <div className="relative">
                <img 
                  src={solution.image}
                  alt={solution.title}
                  className="w-full h-64 object-cover"
                />
                <div className="absolute top-4 left-4 bg-blue-600 text-white p-3 rounded-full">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className={`${solution.icon} text-2xl`}></i>
                  </div>
                </div>
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-800 mb-4">{solution.title}</h3>
                <p className="text-gray-600 text-lg leading-relaxed">{solution.description}</p>
                <div className="mt-6 flex items-center text-blue-600">
                  <div className="w-6 h-6 flex items-center justify-center mr-2">
                    <i className="ri-leaf-line text-lg"></i>
                  </div>
                  <span className="font-medium">Eco-Friendly Solution</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Animated Icons */}
        <div className={`mt-16 flex justify-center space-x-12 transform transition-all duration-1000 delay-800 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          {['ri-sun-line', 'ri-windy-line', 'ri-drop-line', 'ri-leaf-line'].map((icon, index) => (
            <div 
              key={index}
              className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white text-2xl hover:bg-white/30 transition-all duration-300 animate-pulse"
              style={{ animationDelay: `${index * 0.5}s` }}
            >
              <i className={icon}></i>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RenewableEnergy;